'use client';
import React from 'react';
import { MOCK_RESULTS } from './MockExamScreen';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface Props {
  results: typeof MOCK_RESULTS;
}

const PIE_COLORS = ['var(--danger)', 'var(--warning)', 'var(--info)'];

const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number; payload: { pct: number } }> }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg shadow-elevated p-3 text-xs">
      <p className="font-semibold text-foreground">{payload[0].name}</p>
      <p className="text-muted-foreground mt-0.5">{payload[0].value} errors · {payload[0].payload.pct}%</p>
    </div>
  );
};

export default function ReportErrorBreakdown({ results }: Props) {
  const totalErrors = 40 - results.rawScore;
  const pieData = results.errorRootCauses.map((e) => ({
    ...e,
    name: e.cause,
    value: e.count,
  }));

  return (
    <div className="space-y-6 animate-slide-up">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Pie chart */}
        <div className="card-base p-5">
          <h2 className="section-header mb-1">Error Root Cause Distribution</h2>
          <p className="text-xs text-muted-foreground mb-4">{totalErrors} errors classified by cause</p>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={3}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={entry.id} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="circle"
                iconSize={8}
                formatter={(value) => (
                  <span style={{ fontSize: '12px', color: 'var(--muted-foreground)' }}>{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Root cause breakdown */}
        <div className="card-base p-5">
          <h2 className="section-header mb-4">Error Analysis</h2>
          <div className="space-y-4">
            {results.errorRootCauses.map((cause, i) => (
              <div key={cause.id} className="p-4 rounded-xl border border-border">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                    />
                    <p className="text-sm font-semibold text-foreground">{cause.cause}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold tabular-nums text-foreground">{cause.count}</span>
                    <span className="text-xs text-muted-foreground">errors</span>
                    <span className="text-xs font-semibold tabular-nums text-muted-foreground">({cause.pct}%)</span>
                  </div>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden mb-2">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${cause.pct}%`, backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                  />
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {cause.cause === 'Distractor confusion' ?'Selected a plausible-but-wrong option due to surface keyword matching rather than genuine paraphrase recognition. Primary cause: Matching Headings and YN/NG.'
                    : cause.cause === 'Mis-location in passage' ?'Located the wrong paragraph and based the answer on content from a different section. Primary cause: Matching Information questions in Passage 2.' :'Ran out of time and guessed without reading the full question. Occurred on the final 2 questions of Passage 3.'}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Distractor pattern detail */}
      <div className="card-base p-5">
        <h2 className="section-header mb-4">Distractor Confusion Patterns</h2>
        <p className="text-xs text-muted-foreground mb-4">
          63% of your errors were caused by distractor confusion — the highest-impact correction target for reaching Band 8.0
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              id: 'dp-mh',
              type: 'Matching Headings',
              pattern: 'Selecting a heading that matches keywords in one sentence rather than the paragraph\'s overall main idea',
              fix: 'Skim the full paragraph before looking at the heading list. The correct heading summarises the whole paragraph — not just the first sentence.',
              frequency: 'High',
            },
            {
              id: 'dp-ynng',
              type: 'Yes / No / Not Given',
              pattern: 'Marking Not Given when the author\'s qualified language ("contested," "partial") actually expresses a No view',
              fix: 'When the author uses hedging language to cast doubt on a concept, that is a No view — not an absence of view.',
              frequency: 'High',
            },
            {
              id: 'dp-tfng',
              type: 'True / False / Not Given',
              pattern: 'Stopping at the first keyword match and missing a negation in the second half of the clause',
              fix: 'Always read the complete sentence before deciding. Negations typically appear after the main clause.',
              frequency: 'Medium',
            },
            {
              id: 'dp-minfo',
              type: 'Matching Information',
              pattern: 'Selecting a paragraph based on topic association rather than the specific piece of information asked for',
              fix: 'Identify the exact claim in the question, then scan each paragraph for that specific claim — not just the general topic.',
              frequency: 'Low',
            },
          ].map((dp) => (
            <div key={dp.id} className="p-4 rounded-xl bg-muted border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="badge-qtype bg-danger-bg text-danger border border-danger/30">{dp.type}</span>
                <span className={`text-xs font-semibold ${dp.frequency === 'High' ? 'text-danger' : dp.frequency === 'Medium' ? 'text-warning' : 'text-info'}`}>
                  {dp.frequency} frequency
                </span>
              </div>
              <p className="text-xs font-medium text-foreground mb-1.5">Pattern: {dp.pattern}</p>
              <div className="flex items-start gap-1.5 p-2 rounded-lg bg-success-bg border border-accent/20">
                <span className="text-accent text-xs font-bold flex-shrink-0">Fix:</span>
                <p className="text-xs text-foreground leading-relaxed">{dp.fix}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}